import { useEffect, useRef } from "react";
import { useLocation } from "react-router-dom";

/**
 * Thin gold progress bar pinned to the top of the viewport that fills on each
 * client-side route change — the small "something is happening" cue the SPA
 * otherwise lacks. No-op when reduced-motion is set.
 *
 * Mount once near the app root (Layout).
 */
export function useRouteProgress(): void {
  const location = useLocation();
  const barRef = useRef<HTMLDivElement | null>(null);
  const fillRef = useRef<HTMLDivElement | null>(null);
  const timers = useRef<number[]>([]);

  // Create the bar element once.
  useEffect(() => {
    if (
      window.matchMedia &&
      window.matchMedia("(prefers-reduced-motion: reduce)").matches
    ) {
      return;
    }

    const bar = document.createElement("div");
    bar.className = "dag-progress";
    const fill = document.createElement("div");
    fill.className = "dag-progress__fill";
    bar.appendChild(fill);
    document.body.appendChild(bar);
    barRef.current = bar;
    fillRef.current = fill;

    return () => {
      timers.current.forEach((t) => window.clearTimeout(t));
      bar.remove();
      barRef.current = null;
      fillRef.current = null;
    };
  }, []);

  // Animate on every location change (and the initial load).
  useEffect(() => {
    const bar = barRef.current;
    const fill = fillRef.current;
    if (!bar || !fill) return;

    timers.current.forEach((t) => window.clearTimeout(t));
    timers.current = [];

    bar.classList.remove("dag-progress--done");
    fill.style.width = "0%";
    // force a reflow so the width transition restarts cleanly
    fill.getBoundingClientRect();
    const rafId = requestAnimationFrame(() => {
      fill.style.width = "82%";
    });

    timers.current.push(
      window.setTimeout(() => {
        fill.style.width = "100%";
        bar.classList.add("dag-progress--done");
      }, 320)
    );
    timers.current.push(
      window.setTimeout(() => {
        bar.classList.remove("dag-progress--done");
        fill.style.width = "0%";
      }, 720)
    );

    return () => {
      cancelAnimationFrame(rafId);
      timers.current.forEach((t) => window.clearTimeout(t));
      timers.current = [];
    };
  }, [location.pathname, location.search]);
}
